import { useCallback, useEffect } from 'react';
import { formatNumberWithComma } from '../../../util/util';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrderStore } from '../../../store/useOrder';
import {toast} from "react-toastify";

/**
 * @author taulmax
 * @date 2024.12.21
 * @description 주문 확정 모달 - 깔끔하고 정갈한 미니멀 디자인
 */
const ConfirmModal = ({
  isOpen,
  setIsOpen,
  onClickConfirm,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClickConfirm: () => void;
}) => {
  const { contractModalData, resetCheckConfirm } = useOrderStore();

  const onClickCancel = useCallback(() => {
    setIsOpen(false);
    resetCheckConfirm();
  }, [resetCheckConfirm, setIsOpen]);

  const onClickSubmit = useCallback(async () => {
    try {
      await onClickConfirm();
      onClickCancel();
    } catch (error) {
      console.error('주문 확정 처리 중 오류:', error);
      toast.error('주문 확정 처리 중 오류가 발생하였습니다. 잠시 후 재시도해주세요.')
    }
  }, [onClickCancel, onClickConfirm]);

  // ESC key event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClickCancel();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClickCancel]);

  if (!isOpen) return null;

  // contractModalData가 없으면 로딩 상태 표시
  if (!contractModalData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      {/* 배경 클릭 시 닫기 */}
      <div className="absolute inset-0" onClick={onClickCancel} />

      {/* 모달 컨테이너 */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">주문 확정</h2>
          <button
            onClick={onClickCancel}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="p-6 space-y-6">
          {/* 회사 정보 */}
          <div className="text-center">
            <p className="text-gray-600 text-sm mb-2">주문을 확정할 회사</p>
            <p className="text-lg font-medium text-gray-900">
              "{contractModalData.companyName || '회사명 없음'}"
            </p>
          </div>

          {/* 금액 정보 */}
          <div className="space-y-4">
            {/* 현재 잔여 복비 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  현재 잔여 복비
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatNumberWithComma(
                    contractModalData.companyChargeAmount || 0
                  )}
                  원
                </span>
              </div>
            </div>
            {/* 주문 확정 복비 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  주문 확정 복비
                </span>
                <span className="text-lg font-semibold text-red-600">
                  -
                  {formatNumberWithComma(contractModalData.contractAmount || 0)}
                  원
                </span>
              </div>
            </div>

            {/* 실시간 전송 추가요금 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  실시간 전송 추가요금
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  +
                  {contractModalData &&
                    formatNumberWithComma(contractModalData.realTimeSurAmount)}
                  원
                </span>
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-center text-sm text-gray-600">
              위 내용으로 주문을 확정하시겠습니까?
            </p>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className="flex gap-3 p-6 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClickCancel}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors duration-200"
          >
            취소
          </button>
          <button
            onClick={onClickSubmit}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            확정
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
